import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { WifiOff, RefreshCcw } from "lucide-react";

/**
 * NativeBridge — wires Capacitor App/Network plugins into the React app.
 *  - Hardware back button: navigate back; on root, show "tekan sekali lagi untuk keluar"
 *  - Network status: shows persistent banner when offline
 *  - Pull-to-refresh: drag down at top of page to reload the view
 *
 * No-ops gracefully on the web (when not running inside Capacitor).
 */
export default function NativeBridge() {
  const navigate = useNavigate();
  const location = useLocation();
  const [online, setOnline] = useState(true);
  const [pullY, setPullY] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const exitArmed = useRef(false);
  const exitTimer = useRef(null);

  // ---- Capacitor App: back button ----
  useEffect(() => {
    let cleanup;
    (async () => {
      try {
        const { App } = await import("@capacitor/app");
        const sub = await App.addListener("backButton", ({ canGoBack }) => {
          const atRoot =
            location.pathname === "/" || location.pathname === "/dashboard";
          if (atRoot || !canGoBack) {
            if (exitArmed.current) {
              App.exitApp();
              return;
            }
            exitArmed.current = true;
            toast.info("Tekan tombol kembali sekali lagi untuk keluar dari SiMadin");
            if (exitTimer.current) clearTimeout(exitTimer.current);
            exitTimer.current = setTimeout(() => {
              exitArmed.current = false;
            }, 2000);
          } else {
            navigate(-1);
          }
        });
        cleanup = () => sub.remove();
      } catch (_e) {
        // Not running inside Capacitor → ignore.
      }
    })();
    return () => {
      cleanup?.();
      if (exitTimer.current) clearTimeout(exitTimer.current);
    };
  }, [navigate, location.pathname]);

  // ---- Capacitor Network: connectivity status ----
  useEffect(() => {
    let cleanup;
    (async () => {
      try {
        const { Network } = await import("@capacitor/network");
        const status = await Network.getStatus();
        setOnline(status.connected);
        const sub = await Network.addListener(
          "networkStatusChange",
          (s) => setOnline(s.connected),
        );
        cleanup = () => sub.remove();
      } catch (_e) {
        // Fallback to browser navigator events
        const goOnline = () => setOnline(true);
        const goOffline = () => setOnline(false);
        window.addEventListener("online", goOnline);
        window.addEventListener("offline", goOffline);
        setOnline(navigator.onLine);
        cleanup = () => {
          window.removeEventListener("online", goOnline);
          window.removeEventListener("offline", goOffline);
        };
      }
    })();
    return () => cleanup?.();
  }, []);

  // ---- Pull-to-refresh ----
  const startY = useRef(0);
  const onTouchStart = useCallback((e) => {
    if (window.scrollY > 0) return;
    startY.current = e.touches[0].clientY;
  }, []);
  const onTouchMove = useCallback((e) => {
    if (refreshing || window.scrollY > 0 || startY.current === 0) return;
    const dy = e.touches[0].clientY - startY.current;
    if (dy > 0) setPullY(Math.min(dy * 0.5, 120));
  }, [refreshing]);
  const onTouchEnd = useCallback(() => {
    if (pullY > 80 && !refreshing) {
      setRefreshing(true);
      setPullY(40);
      setTimeout(() => {
        window.location.reload();
      }, 250);
    } else {
      setPullY(0);
    }
    startY.current = 0;
  }, [pullY, refreshing]);

  useEffect(() => {
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: true });
    window.addEventListener("touchend", onTouchEnd);
    return () => {
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
    };
  }, [onTouchStart, onTouchMove, onTouchEnd]);

  return (
    <>
      {!online && (
        <div
          data-testid="offline-banner"
          className="fixed top-0 inset-x-0 z-[9998] bg-red-600 text-white px-3 py-2 text-xs font-semibold flex items-center justify-center gap-2 shadow-md"
          style={{ paddingTop: "calc(env(safe-area-inset-top, 0) + 0.5rem)" }}
        >
          <WifiOff className="h-4 w-4" />
          Tidak ada koneksi internet. Mohon periksa Wi-Fi atau data seluler Anda.
        </div>
      )}
      {pullY > 0 && (
        <div
          className="fixed left-0 right-0 z-[9997] flex justify-center pointer-events-none"
          style={{ top: 0, transform: `translateY(${pullY - 40}px)` }}
        >
          <div className="bg-white rounded-full shadow-lg h-10 w-10 flex items-center justify-center border border-emerald-100">
            <RefreshCcw
              className={`h-5 w-5 text-emerald-600 ${refreshing ? "animate-spin" : ""}`}
              style={{ transform: refreshing ? undefined : `rotate(${pullY * 4}deg)` }}
            />
          </div>
        </div>
      )}
    </>
  );
}
